import React, { useState } from 'react';
import { IoClose, IoMailOutline, IoCopyOutline, IoCheckmarkCircle } from 'react-icons/io5';
import axiosInstance from '../../api/axiosInstance';

interface InviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onInviteSent?: () => void;
}

export const InviteModal: React.FC<InviteModalProps> = ({ isOpen, onClose, onInviteSent }) => {
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteSent, setInviteSent] = useState<string | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = inviteEmail.trim();
    if (!email) return;
    
    setInviteLoading(true);
    setInviteError(null);
    setInviteSent(null);
    
    try {
      const { data } = await axiosInstance.post<{ inviteLink: string; receiverEmail: string }>('/api/invite', {
        receiverEmail: email,
      });
      setInviteSent(data.inviteLink);
      setInviteEmail('');
      onInviteSent?.();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Failed to send invite.';
      setInviteError(msg);
    } finally {
      setInviteLoading(false);
    }
  };

  const handleCopyLink = async () => {
    if (!inviteSent) return;
    try {
      await navigator.clipboard.writeText(inviteSent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleClose = () => {
    setInviteEmail('');
    setInviteError(null);
    setInviteSent(null);
    setCopied(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div 
          className="bg-[#1C2731] rounded-2xl shadow-2xl w-full max-w-md border border-slate-700/50 overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-slate-700/50">
            <h2 className="text-xl font-semibold text-white flex items-center gap-2">
              <IoMailOutline className="text-emerald-400" size={24} />
              Invite a Friend
            </h2>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-slate-700/50 rounded-lg transition-colors text-gray-400 hover:text-white"
            >
              <IoClose size={22} />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {!inviteSent ? (
              <form onSubmit={handleInvite} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Friend's Email Address
                  </label>
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="friend@example.com"
                    className="w-full px-4 py-3 bg-[#0B141A] border border-slate-600 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                    required
                    disabled={inviteLoading}
                  />
                </div>

                {inviteError && (
                  <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm">
                    {inviteError}
                  </div>
                )}

                <div className="flex gap-3 pt-2">
                  <button
                    type="submit"
                    disabled={inviteLoading || !inviteEmail.trim()}
                    className="flex-1 px-4 py-3 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-xl transition-all flex items-center justify-center gap-2"
                  >
                    {inviteLoading ? (
                      <>
                        <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sending...
                      </>
                    ) : (
                      'Send Invite'
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-4 py-3 bg-slate-700 hover:bg-slate-600 text-white font-medium rounded-xl transition-all"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-center p-4 bg-emerald-500/10 rounded-xl border border-emerald-500/30">
                  <IoCheckmarkCircle className="text-emerald-400" size={32} />
                </div>
                <div className="text-center">
                  <h3 className="text-lg font-semibold text-white mb-2">Invite Sent!</h3>
                  <p className="text-sm text-gray-400 mb-4">
                    Share this link with your friend to invite them to chat:
                  </p>
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={inviteSent}
                    readOnly
                    className="flex-1 px-4 py-3 bg-[#0B141A] border border-slate-600 rounded-xl text-white text-sm break-all"
                  />
                  <button
                    onClick={handleCopyLink}
                    className="px-4 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl transition-all flex items-center gap-2"
                    title="Copy link"
                  >
                    {copied ? (
                      <IoCheckmarkCircle size={20} className="text-emerald-400" />
                    ) : (
                      <IoCopyOutline size={20} />
                    )}
                  </button>
                </div>
                <button
                  onClick={handleClose}
                  className="w-full px-4 py-3 bg-emerald-600 hover:bg-emerald-500 text-white font-medium rounded-xl transition-all"
                >
                  Done
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
